import Highcharts from 'highcharts';

/**
 * Shadcn-vue compatible Highcharts theme
 * Uses CSS variables directly for perfect compatibility
 */

// Helper function to get CSS variable values
const getCSSVariable = (variable) => {
    if (typeof window !== 'undefined') {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    }
    return null;
};

// Simple theme color getter - TRUST the CSS variables
const getThemeColor = (cssVar) => {
    // Always try CSS variables first and trust them
    if (typeof window !== 'undefined') {
        const computedValue = getCSSVariable(cssVar);
        if (computedValue && computedValue !== '') {
            return computedValue;
        }
    }

    // Return empty string if CSS variable not available - let theme handle defaults
    return '';
};

// Get current theme colors from CSS variables
const getThemeColors = () => {
    return {
        // Core colors - trust CSS variables
        background: getThemeColor('--background'),
        foreground: getThemeColor('--foreground'),
        primary: getThemeColor('--primary'),
        primaryForeground: getThemeColor('--primary-foreground'),
        secondary: getThemeColor('--secondary'),
        secondaryForeground: getThemeColor('--secondary-foreground'),
        accent: getThemeColor('--accent'),
        accentForeground: getThemeColor('--accent-foreground'),
        muted: getThemeColor('--muted'),
        mutedForeground: getThemeColor('--muted-foreground'),
        card: getThemeColor('--card'),
        cardForeground: getThemeColor('--card-foreground'),
        border: getThemeColor('--border'),
        input: getThemeColor('--input'),
        ring: getThemeColor('--ring'),
        destructive: getThemeColor('--destructive'),
        destructiveForeground: getThemeColor('--destructive-foreground'),

        // Chart colors - use CSS variables first, then fallbacks
        chart1: getThemeColor('--chart-1'),
        chart2: getThemeColor('--chart-2'),
        chart3: getThemeColor('--chart-3'),
        chart4: getThemeColor('--chart-4'),
        chart5: getThemeColor('--chart-5')
    };
};

// Create the shadcn theme
export const createShadcnTheme = () => {
    const colors = getThemeColors();

    return {
        // Use chart colors from CSS, then diverse fallbacks
        colors: [
            colors.chart1,
            colors.chart2,
            colors.chart3,
            colors.chart4,
            colors.chart5,
            // Additional diverse fallback colors (extended palette)
            'hsl(12 76% 61%)', // Orange-red
            'hsl(213 93% 68%)', // Blue
            'hsl(142 71% 45%)', // Green
            'hsl(288 89% 58%)', // Purple
            'hsl(48 96% 53%)', // Yellow
            'hsl(280 100% 70%)', // Magenta
            'hsl(188 95% 68%)', // Cyan
            'hsl(25 95% 53%)', // Orange
            'hsl(221 83% 53%)', // Indigo
            'hsl(332 81% 62%)', // Pink
            'hsl(173 58% 39%)', // Teal
            'hsl(43 74% 66%)', // Amber
            'hsl(262 83% 58%)', // Violet
            'hsl(204 94% 94%)', // Sky blue
            'hsl(159 64% 52%)', // Emerald
            'hsl(20 90% 48%)', // Red-orange
            'hsl(217 91% 60%)', // Blue variant
            'hsl(142 76% 36%)', // Forest green
            'hsl(291 95% 71%)', // Light purple
            'hsl(45 93% 47%)' // Gold
        ],

        chart: {
            backgroundColor: colors.card,
            borderColor: colors.border,
            borderRadius: 8,
            borderWidth: 1,
            className: 'highcharts-shadcn-theme',
            plotBackgroundColor: 'transparent',
            plotBorderColor: colors.border,
            plotBorderWidth: 0,
            spacing: [16, 16, 16, 16], // top, right, bottom, left padding (p-4 = 16px)
            style: {
                fontFamily: 'var(--font-sans, ui-sans-serif, system-ui, sans-serif)',
                fontSize: '13px',
                color: colors.cardForeground,
                lineHeight: '1.5'
            }
        },

        title: {
            style: {
                color: colors.foreground,
                fontSize: '18px', // text-lg equivalent
                fontWeight: '600', // font-semibold
                lineHeight: '1.4'
            },
            margin: 30
        },

        subtitle: {
            style: {
                color: colors.mutedForeground,
                fontSize: '14px', // text-sm
                fontWeight: '400', // font-normal
                lineHeight: '1.4'
            }
        },

        xAxis: {
            gridLineColor: colors.border,
            lineColor: colors.border,
            tickColor: colors.border,
            title: {
                style: {
                    color: colors.mutedForeground,
                    fontSize: '12px', // text-xs
                    fontWeight: '500', // font-medium
                    lineHeight: '1.4'
                }
            },
            labels: {
                style: {
                    color: colors.mutedForeground,
                    fontSize: '11px', // text-xs
                    fontWeight: '400', // font-normal
                    lineHeight: '1.4'
                }
            }
        },

        yAxis: {
            gridLineColor: colors.border,
            lineColor: colors.border,
            tickColor: colors.border,
            title: {
                style: {
                    color: colors.mutedForeground,
                    fontSize: '12px', // text-xs
                    fontWeight: '500', // font-medium
                    lineHeight: '1.4'
                }
            },
            labels: {
                style: {
                    color: colors.mutedForeground,
                    fontSize: '11px', // text-xs
                    fontWeight: '400', // font-normal
                    lineHeight: '1.4'
                }
            }
        },

        tooltip: {
            backgroundColor: colors.primary,
            borderColor: 'transparent',
            borderRadius: 6,
            borderWidth: 0,
            shadow: true,
            style: {
                color: colors.primaryForeground,
                fontSize: '12px',
                fontFamily: 'var(--font-sans, ui-sans-serif, system-ui, sans-serif)',
                fontWeight: '400',
                lineHeight: '1.4'
            },
            headerFormat: '<div style="font-size:12px;font-weight:600;margin-bottom:4px;color:' + colors.primaryForeground + '">{point.key}</div>',
            pointFormatter: function () {
                let formattedValue = this.y;
                try {
                    if (window.formatter && typeof window.formatter === 'function') {
                        formattedValue = window.formatter(this.y).display;
                    }
                } catch (e) {
                    if (this.y % 1 !== 0) {
                        formattedValue = this.y.toFixed(2);
                    } else {
                        formattedValue = this.y.toLocaleString();
                    }
                }

                // Get the colors object from the current theme
                const currentColors = getThemeColors();
                const primaryForeground = currentColors.primaryForeground;
                return (
                    '<div style="color:' +
                    primaryForeground +
                    ';margin:2px 0;display:flex;align-items:center;gap:6px">' +
                    '<span style="width:8px;height:8px;border-radius:50%;background:' +
                    this.color +
                    ';display:inline-block"></span>' +
                    '<span>' +
                    this.series.name +
                    ':</span>' +
                    '<strong style="color:' +
                    primaryForeground +
                    '">' +
                    formattedValue +
                    '</strong>' +
                    '</div>'
                );
            },
            footerFormat: '',
            shared: true,
            useHTML: true
        },

        legend: {
            backgroundColor: 'transparent',
            itemStyle: {
                color: colors.foreground,
                fontSize: '12px',
                fontWeight: '400'
            },
            itemHiddenStyle: {
                color: colors.mutedForeground
            }
        },

        credits: {
            enabled: false
        },

        plotOptions: {
            series: {
                borderRadius: 4,
                borderWidth: 0,
                dataLabels: {
                    color: colors.foreground,
                    style: {
                        fontSize: '11px',
                        fontWeight: '500',
                        textOutline: 'none'
                    }
                },
                marker: {
                    lineColor: 'transparent',
                    lineWidth: 0,
                    radius: 4
                }
            },

            column: {
                borderRadius: 6,
                borderWidth: 0,
                pointPadding: 0.15,
                groupPadding: 0.1
            },

            bar: {
                borderRadius: 6,
                borderWidth: 0,
                pointPadding: 0.15,
                groupPadding: 0.1
            },

            line: {
                lineWidth: 2,
                marker: {
                    enabled: true,
                    radius: 4,
                    lineWidth: 0,
                    lineColor: 'transparent'
                }
            },

            area: {
                lineWidth: 2,
                fillOpacity: 0.6,
                marker: {
                    enabled: false,
                    radius: 4,
                    lineWidth: 0,
                    lineColor: 'transparent'
                }
            },

            pie: {
                borderColor: colors.background,
                borderWidth: 2,
                dataLabels: {
                    color: colors.foreground,
                    style: {
                        fontSize: '11px',
                        fontWeight: '500'
                    }
                }
            }
        }
    };
};

// Function to apply the theme
export const applyShadcnTheme = () => {
    const theme = createShadcnTheme();

    // Apply theme globally
    Highcharts.setOptions(theme);

    // Force update any existing charts
    if (typeof window !== 'undefined' && Highcharts.charts) {
        Highcharts.charts.forEach((chart) => {
            if (chart) {
                chart.update(theme, true);
            }
        });
    }
};

// Function to dynamically update theme when dark mode changes
export const updateThemeForDarkMode = () => {
    applyShadcnTheme();
};

// Function to check if CSS variables are loaded
const areCSSVariablesLoaded = () => {
    if (typeof window === 'undefined') return false;

    // Check for key CSS variables
    const testVars = ['--background', '--foreground', '--primary', '--chart-1'];
    return testVars.every((cssVar) => {
        const value = getCSSVariable(cssVar);
        return value && value.trim() !== '';
    });
};

// Apply theme with CSS readiness check
const applyThemeWhenReady = () => {
    if (areCSSVariablesLoaded()) {
        applyShadcnTheme();
    } else {
        // Retry after a short delay
        setTimeout(applyThemeWhenReady, 50);
    }
};

// Auto-detect and apply theme on load
if (typeof window !== 'undefined') {
    // Apply theme when DOM is ready and CSS is loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyThemeWhenReady);
    } else {
        // DOM already ready, but CSS might still be loading
        applyThemeWhenReady();
    }

    // Also try after a small delay to be extra sure
    setTimeout(applyThemeWhenReady, 100);

    // Additional fallback - reapply theme after a longer delay to catch any late-loading CSS
    setTimeout(() => {
        if (areCSSVariablesLoaded()) {
            applyShadcnTheme();
        }
    }, 500);

    // Watch for dark mode changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addListener(updateThemeForDarkMode);

    // Watch for manual theme changes on document class
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (
                mutation.type === 'attributes' &&
                mutation.attributeName === 'class' &&
                mutation.target.classList.contains('dark') !== mutation.oldValue?.includes('dark')
            ) {
                updateThemeForDarkMode();
            }
        });
    });

    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
    });
}

export default {
    createShadcnTheme,
    applyShadcnTheme,
    updateThemeForDarkMode
};
